import React, { useRef, useEffect } from 'react';

const NEURON_COLORS = ['#A78BFA', '#818CF8', '#C4B5FD', '#E879F9'];
const FIRE_COLOR    = '#F0ABFC';
const SIGNAL_COLOR  = '#FCD34D';

function spawnNeuron(W, H) {
  return {
    x:          60 + Math.random() * (W - 120),
    y:          60 + Math.random() * (H - 120),
    vx:         (Math.random() - 0.5) * 0.4,
    vy:         (Math.random() - 0.5) * 0.4,
    size:       2 + Math.random() * 3,
    color:      NEURON_COLORS[Math.floor(Math.random() * NEURON_COLORS.length)],
    charge:     Math.random(),
    chargeRate: 0.001 + Math.random() * 0.002,
    fireGlow:   0,
    life:       Math.random() * 0.5,
    maxLife:    0.7 + Math.random() * 0.3,
    speed:      0.3 + Math.random() * 0.5,
  };
}

export default function NeuralBg() {
  const canvasRef = useRef(null);
  const mouse     = useRef({ x: null, y: null });
  const neurons   = useRef([]);
  const signals   = useRef([]);
  const raf       = useRef(null);
  const t         = useRef(0);
  const dims      = useRef({ W: window.innerWidth, H: window.innerHeight });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');

    const resize = () => {
      dims.current = { W: window.innerWidth, H: window.innerHeight };
      canvas.width  = dims.current.W;
      canvas.height = dims.current.H;
    };
    resize();
    window.addEventListener('resize', resize);

    const COUNT = Math.max(70, Math.floor((dims.current.W * dims.current.H) / 14000));
    neurons.current = Array.from({ length: COUNT }, () =>
      spawnNeuron(dims.current.W, dims.current.H)
    );

    const onMove = (e) => { mouse.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener('mousemove', onMove);

    const draw = () => {
      t.current += 0.011;
      const time = t.current;
      const { x: mx, y: my } = mouse.current;
      const { W, H } = dims.current;
      const ns = neurons.current;

      ctx.clearRect(0, 0, W, H);

      // ── update neurons ────────────────────────────────────────────────────
      ns.forEach((n, i) => {
        // fluid noise steering
        const nx = Math.sin(n.x * 0.005 + time * 0.32) * Math.cos(n.y * 0.007 + time * 0.19);
        const ny = Math.cos(n.x * 0.007 - time * 0.25) * Math.sin(n.y * 0.005 + time * 0.35);
        n.vx += nx * 0.018;
        n.vy += ny * 0.018;

        // soft wall bounce
        if (n.x < 50)      n.vx += 0.06;
        if (n.x > W - 50)  n.vx -= 0.06;
        if (n.y < 50)      n.vy += 0.06;
        if (n.y > H - 50)  n.vy -= 0.06;

        // cursor repulsion
        if (mx !== null) {
          const mdx = n.x - mx, mdy = n.y - my;
          const md  = Math.sqrt(mdx * mdx + mdy * mdy);
          if (md < 110 && md > 0) {
            const f = ((110 - md) / 110) * 0.65;
            n.vx += (mdx / md) * f;
            n.vy += (mdy / md) * f;
          }
        }

        n.vx *= 0.94; n.vy *= 0.94;
        n.x  += n.vx * n.speed;
        n.y  += n.vy * n.speed;

        // charge → fire
        n.charge += n.chargeRate;
        if (n.charge >= 1) {
          n.charge   = 0;
          n.fireGlow = 1;
          ns.forEach((target, j) => {
            if (j === i) return;
            const sdx = n.x - target.x, sdy = n.y - target.y;
            const sd  = Math.sqrt(sdx * sdx + sdy * sdy);
            if (sd < 130) {
              signals.current.push({
                x: n.x, y: n.y,
                tx: target.x, ty: target.y,
                progress: 0,
                speed: 0.015 + Math.random() * 0.012,
              });
              target.charge = Math.min(target.charge + 0.25, 0.93);
            }
          });
        }

        n.fireGlow = Math.max(0, n.fireGlow - 0.034);
        n.life += 0.004;
        if (n.life >= n.maxLife) {
          ns[i] = spawnNeuron(W, H);
        }
      });

      // ── axon lines ────────────────────────────────────────────────────────
      for (let i = 0; i < ns.length; i++) {
        for (let j = i + 1; j < ns.length; j++) {
          const a = ns[i], b = ns[j];
          const dx   = a.x - b.x, dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 130) {
            const glow  = Math.max(a.fireGlow, b.fireGlow);
            const baseA = (1 - dist / 130) * 0.18;
            ctx.strokeStyle = `rgba(167,139,250,${baseA + glow * 0.45})`;
            ctx.lineWidth   = 0.5 + glow * 1.4;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // ── synaptic pulses ───────────────────────────────────────────────────
      const alive = [];
      signals.current.forEach(s => {
        s.progress += s.speed;
        if (s.progress >= 1) return;
        alive.push(s);
        const px    = s.x  + (s.tx - s.x)  * s.progress;
        const py    = s.y  + (s.ty - s.y)  * s.progress;
        const alpha = Math.sin(s.progress * Math.PI);

        ctx.globalAlpha = alpha * 0.32;
        ctx.fillStyle   = SIGNAL_COLOR;
        ctx.beginPath(); ctx.arc(px, py, 9, 0, Math.PI * 2); ctx.fill();

        ctx.globalAlpha = alpha * 0.75;
        ctx.beginPath(); ctx.arc(px, py, 4, 0, Math.PI * 2); ctx.fill();

        ctx.globalAlpha = alpha * 0.95;
        ctx.fillStyle   = '#fff';
        ctx.beginPath(); ctx.arc(px, py, 1.8, 0, Math.PI * 2); ctx.fill();

        ctx.globalAlpha = 1;
      });
      signals.current = alive;

      // ── neuron bodies ─────────────────────────────────────────────────────
      ns.forEach(n => {
        const la   = Math.sin((n.life / n.maxLife) * Math.PI);
        const glow = n.fireGlow;
        const col  = glow > 0.6 ? FIRE_COLOR : n.color;

        ctx.globalAlpha = la * (0.1 + glow * 0.35);
        ctx.fillStyle   = col;
        ctx.beginPath(); ctx.arc(n.x, n.y, n.size * (4 + glow * 5), 0, Math.PI * 2); ctx.fill();

        ctx.globalAlpha = la * (0.6 + glow * 0.4);
        ctx.fillStyle   = col;
        ctx.beginPath(); ctx.arc(n.x, n.y, n.size * (1 + glow * 0.6), 0, Math.PI * 2); ctx.fill();

        ctx.globalAlpha = la * (0.5 + glow * 0.5);
        ctx.fillStyle   = '#fff';
        ctx.beginPath(); ctx.arc(n.x, n.y, n.size * 0.42, 0, Math.PI * 2); ctx.fill();

        ctx.globalAlpha = 1;
      });

      raf.current = requestAnimationFrame(draw);
    };

    raf.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 1,
      }}
    />
  );
}
